import { type FC, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  useBuildWorkshopMutation,
  useGetWorkshopQuery,
} from '@api/queries/workshop';
import { AppRoute } from '@common/app/routes';
import FirstBuild from '@common/components/FirstBuild/FirstBuild';
import { useCheckRequirements } from '@common/hooks/check-requirements';
import { updateStorageAndBalance } from '@redux/reducers/userReducer';
import { selectNextToBuildWorkshopRequirementsList } from '@redux/selectors/workshopSelectors';
import { useAppDispatch, useAppSelector } from '@redux/store';

import { completeNewLevelStep } from '@/common/lib/completeNewLevelStep';
import { firebaseLogUpgradeBuilding } from '@/common/lib/firebaseLog';
import { SendAnalitics } from '@/common/lib/playdeck';
import { type GoodsType } from '@/common/types/goods';
import { selectNextStepPlant } from '@/redux/selectors/userSelectors';

const WorkshopBuild: FC = () => {
  const [skip, setSkip] = useState(false);
  useGetWorkshopQuery(undefined, { skip, refetchOnMountOrArgChange: true });
  const navigate = useNavigate();
  const [buildWorkshop, { isLoading }] = useBuildWorkshopMutation();
  const isFirstBuild = useAppSelector(
    (state) => state.workshop.list?.length === 0,
  );
  const nextToBuild = useAppSelector((state) => state.workshop.nextToBuild);
  const requirementsList = useAppSelector(
    selectNextToBuildWorkshopRequirementsList,
  );
  const currentStep = useAppSelector(selectNextStepPlant);
  const dispatch = useAppDispatch();
  const onBuild = useCallback((): void => {
    if (!nextToBuild) return;

    const { id: item_id, name: item, level: item_level } = nextToBuild;

    setSkip(true);
    buildWorkshop({ id: item_id }).then(() => {
      dispatch(updateStorageAndBalance(requirementsList));
      SendAnalitics.buildDone({
        type: 'goods',
        item_id,
        item: item as GoodsType,
        item_level,
      });
      completeNewLevelStep({
        data: { name: nextToBuild?.name, level: 1 },
        dispatch,
        currentStep: {
          id: currentStep?.id,
          name: currentStep?.name,
          level: currentStep?.level,
        },
      });
      // if (nextToBuild?.level === 1) {
      //   const workshop = response.data?.find(
      //     ({ name }) => name === nextToBuild.name,
      //   );
      //   openModal({
      //     renderKind: 'openNewGoods',
      //     props: { id: workshop?.id as string },
      //   });
      // }

      navigate(AppRoute.WORKSHOP + `/${nextToBuild?.name}`);
    });

    if (nextToBuild && nextToBuild.creationRequirements)
      firebaseLogUpgradeBuilding(
        {
          name: nextToBuild.name,
          id: nextToBuild.id,
          level: nextToBuild.level,
        },
        nextToBuild.creationRequirements,
      );
  }, [nextToBuild?.id, buildWorkshop, requirementsList, dispatch, navigate]);
  const { isComplete } = useCheckRequirements(
    nextToBuild?.creationRequirements,
  );

  if (!nextToBuild || !requirementsList) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Workshop_Build</title>
      </Helmet>
      <FirstBuild
        isComplete={isComplete}
        requirementsList={requirementsList}
        kind="workshop"
        isFirstBuild={isFirstBuild}
        name={nextToBuild.name}
        onBuild={onBuild}
        isLoading={isLoading}
      />
    </>
  );
};

export { WorkshopBuild };
